import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NbCardModule, NbDatepickerModule, NbDialogRef, NbDialogService, NbInputModule, NbOptionModule, NbSelectModule } from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { DocumentsService, DocumentTypesService, InvestmentsService, SaveToastService } from '../../../../@core/utils';
import { APIService, KPI } from '../../../../@core/api';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { UpperCasePipe } from '@angular/common';
import { LinkedEquipmentDialogComponent } from '../../linked-equipment-dialog/linked-equipment-dialog.component';
import { RightPanelDialogService } from '../../../../@core/utils/right-panel-dialog.service';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { first } from 'rxjs/operators';
import { toDateValue, toIsoDate } from '../../../utils/date.utils';

@Component({
  selector: 'app-add-document-dialog',
  templateUrl: './add-document-dialog.component.html',
  styleUrls: ['./add-document-dialog.component.scss'],
  standalone: true,
  imports: [
    NbCardModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    
    NbSelectModule,
    NbOptionModule,
    NbInputModule,
    NbDatepickerModule,
    UpperCasePipe,
  ],
})
export class AddDocumentDialogComponent implements OnInit, OnDestroy {

  constructor(
    private ref: NbDialogRef<AddDocumentDialogComponent>,
    private investmentsService: InvestmentsService,
    private documentsTypeService: DocumentTypesService,
    private documentsService: DocumentsService,
    private dialogService: NbDialogService,
    private cdr: ChangeDetectorRef,
    private api: APIService,
    private translate: TranslateService,
    private sideFormLevelService: RightPanelDialogService,
    private saveToast: SaveToastService,
  ) {
    this.form = new UntypedFormGroup({
      label: new FormControl<string>('', [Validators.required, Validators.maxLength(200)]),
      docType: new FormControl<number>({ value: null, disabled: true }, [Validators.required]),
      investId: new FormControl<number>(null),
      url: new FormControl<string>('', [Validators.required, Validators.maxLength(300), Validators.pattern(/^(ftp|http|https):\/\/[^ "]+$/)]),
      company: new FormControl<string>(''),
      duration: new FormControl<string>(''),
      expiry_date: new FormControl(null),
    });
  }

  @Input() public investIds: number[] = [];
  @Input() public assetIds: number[] = [];
  @Input() public actionIds: number[] = [];
  @Input() public perimeterIds: number[] = [];
  @Input() public perimeterLabel: string = '';
  @Input() public linkedToLabel: string = '';
  @Input() public siteId: number | null = null;
  @Input() public initialLabel: string = '';
  @Input() public isEditing: boolean = false;
  @Input() public editDoc: KPI.Document | null = null;
  private subscriptions: Subscription[] = [];
  public investments: KPI.Investment[] = [];
  public loading: boolean = false;
  public loadingDocTypes: boolean = false;
  private saveStatusTimer: any = null;
  private savingStartTime: number = 0;
  public step: number = 1;
  public form: UntypedFormGroup;
  public documentTypes: KPI.DocumentType[] = [];
  public corporateNames: string[] = [];
  public filteredCorporates: string[] = [];
  public linkedEquipmentIds: number[] = [];
  private isConfirmDialogOpen: boolean = false;
  private readonly toDateValue = toDateValue;
  private readonly toIsoDate = toIsoDate;

  public get isPerimeterMode(): boolean {
    return this.investIds.length === 0 && this.assetIds.length === 0 && this.actionIds.length === 0 && this.perimeterIds.length > 0;
  }

  public get isAssetMode(): boolean {
    return this.assetIds.length > 0;
  }

  public get isActionMode(): boolean {
    return this.actionIds.length > 0 && this.assetIds.length === 0;
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.api.get_corporates().subscribe(corporates => {
        this.corporateNames = corporates.map(c => c.name);
        this.cdr.detectChanges();
      })
    );
    this.subscriptions.push(
      (this.ref as any).onBackdropClick.subscribe(() => this.confirmClose())
    );

    setTimeout(() => {
      if (this.isEditing && this.editDoc) {
        this.form.get('label').setValue(this.editDoc.title || '');
        this.form.get('url').setValue(this.editDoc.link || '');
        this.form.get('url').clearValidators();
        this.form.get('url').setValidators([Validators.maxLength(300)]);
        this.form.get('url').updateValueAndValidity({ emitEvent: false });
        if (this.isMaintenanceContract) {
          this.form.get('company').setValue(this.editDoc.company || '');
          this.form.get('expiry_date').setValue(this.toDateValue(this.editDoc.expiry_date));
          this.linkedEquipmentIds = this.editDoc.linked_equipment || [];
          this.step = 2;
        }
        this.cdr.detectChanges();
        return;
      }
      if (this.initialLabel) {
        this.form.get('label').setValue(this.initialLabel);
      }
      if (this.isAssetMode) {
        this.form.get('docType').enable();
        this.loadEntityDocumentTypes('asset');
      } else if (this.isActionMode) {
        this.form.get('docType').enable();
        this.loadEntityDocumentTypes('initiative');
      } else if (this.isPerimeterMode) {
        this.form.get('docType').enable();
        this.loadPerimeterDocumentTypes();
      } else {
        this.form.get('investId').setValidators([Validators.required]);
        this.form.get('investId').updateValueAndValidity({ emitEvent: false });
        this.subscriptions.push(
          this.form.get('investId').valueChanges.subscribe(investId => {
            if (investId) {
              this.form.get('docType').setValue(null);
              this.form.get('docType').enable();
              this.documentTypes = [];
              this.loadInvestmentDocumentTypes(investId);
            } else {
              this.form.get('docType').setValue(null);
              this.form.get('docType').disable();
              this.documentTypes = [];
            }
          })
        );
        this.loading = true;
        this.subscriptions.push(
          this.investmentsService.getInvestmentsByIds(this.investIds).subscribe((investments) => {
            this.loading = false;
            this.investments = investments.sort((a, b) => a.label.localeCompare(b.label));
            if (this.investments.length === 1) {
              this.form.get('investId').setValue(this.investments[0].id);
            }
            this.cdr.detectChanges();
          })
        );
      }
      this.cdr.detectChanges();
    });
  }

  private loadInvestmentDocumentTypes(investId: number): void {
    this.loadingDocTypes = true;
    this.subscriptions.push(
      this.documentsTypeService.getDefaultDocumentTypesForInvestment(investId).subscribe((defaultdocRequirement) => {
        const seenIds = new Set<number>();
        this.documentTypes = [];
        defaultdocRequirement.forEach((req) => {
          if (req.document_type && !seenIds.has(req.document_type.id)) {
            seenIds.add(req.document_type.id);
            this.documentTypes.push(req.document_type);
          }
        });
        this.loadingDocTypes = false;
        this.cdr.detectChanges();
      })
    );
  }

  private loadEntityDocumentTypes(entityType: 'asset' | 'initiative'): void {
    this.loadingDocTypes = true;
    const entityId = entityType === 'asset' ? this.assetIds[0] : this.actionIds[0];
    const source$ = entityType === 'asset'
      ? this.documentsTypeService.getDefaultDocumentTypesForAsset(entityId)
      : this.documentsTypeService.getDefaultDocumentTypesForInitiative(entityId);
    this.subscriptions.push(
      source$.subscribe((requirements) => {
        const seenIds = new Set<number>();
        this.documentTypes = [];
        requirements.forEach((req: any) => {
          const dt = req.document_type;
          if (dt && !seenIds.has(dt.id)) {
            seenIds.add(dt.id);
            this.documentTypes.push(dt);
          }
        });
        this.loadingDocTypes = false;
        this.cdr.detectChanges();
      })
    );
  }

  private loadPerimeterDocumentTypes(): void {
    this.loadingDocTypes = true;
    this.subscriptions.push(
      this.documentsTypeService.getDocumentTypesForPerimeter(this.perimeterIds[0]).subscribe((types) => {
        this.documentTypes = types;
        this.loadingDocTypes = false;
        this.cdr.detectChanges();
      })
    );
  }

  public get isMaintenanceContract(): boolean {
    if (this.isEditing) {
      return !!this.editDoc?.is_maintenance_contract || this.editDoc?.type === 'Contrat de maintenance';
    }
    const selectedTypeId = this.form?.get('docType')?.value;
    const found = this.documentTypes.find(dt => dt.id === selectedTypeId);
    return !!found?.is_maintenance_contract;
  }

  public isStep1Valid(): boolean {
    const label = this.form.get('label');
    const url = this.form.get('url');
    const docTypeValue = this.form.get('docType').value;
    if (this.isPerimeterMode || this.isAssetMode || this.isActionMode) {
      return label.valid && !!docTypeValue && url.valid;
    }
    const investId = this.form.get('investId');
    return label.valid && !!docTypeValue && investId.valid && url.valid;
  }

  public nextOrSubmit(): void {
    if (!this.isStep1Valid()) {
      return;
    }
    if (this.isMaintenanceContract) {
      this.step = 2;
    } else {
      this.addDocument();
    }
  }

  public previousStep(): void {
    this.step = 1;
    this.linkedEquipmentIds = [];
  }

  public openEquipmentSelection(): void {
    this.sideFormLevelService.open(LinkedEquipmentDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasScroll: false,
      hasBackdrop: true,
      backdropClass: 'control-report-backdrop',
      dialogClass: 'doc-linked-equipment-overlay',
      context: {
        documentId: -1,
        reportId: null,
        asset: null,
        siteId: this.siteId,
        linkedEquipmentIds: this.linkedEquipmentIds,
        title: this.translate.instant('documents.sheet.linked_equipments_maintenance_title'),
      } as any,
    }).onClose.pipe(first()).subscribe((result: number[] | boolean) => {
      if (Array.isArray(result)) {
        this.linkedEquipmentIds = result;
      }
    });
  }

  public addDocument(): void {
    this.loading = true;
    this.setSaving();
    const values = this.form.getRawValue();

    if (this.isEditing && this.editDoc) {
      const patch: Partial<KPI.Document> = {
        title: values.label,
        link: values.url,
      };
      if (this.isMaintenanceContract) {
        patch.company = values.company || '';
        patch.expiry_date = this.toIsoDate(values.expiry_date);
        patch.linked_equipment = this.linkedEquipmentIds;
      }
      this.api.documents_patch(this.editDoc.id, patch).subscribe({
        next: (updatedDoc) => {
          this.loading = false;
          this.documentsService.clearDocumentsCache();
          this.documentsService.documentsHaveChanged();
          this.setSavedAndClose(updatedDoc);
        },
        error: () => {
          this.loading = false;
          this.saveToast.close();
          this.cdr.detectChanges();
        },
      });
      return;
    }

    const document: any = {
      title: values.label,
      link: values.url,
      equipment: '',
      comments: '',
      expiry_date: null,
    };

    if (this.isMaintenanceContract) {
      document.company = values.company || '';
      document.duration = values.duration || '';
      document.expiry_date = this.toIsoDate(values.expiry_date);
      document.linked_equipment = this.linkedEquipmentIds;
    }

    const params = {
      action: 'create',
      document_type: values.docType,
      document,
    };

    let request$: ReturnType<typeof this.documentsService.addDocument>;
    if (this.isAssetMode) {
      request$ = this.documentsService.addAssetDocument(this.assetIds[0], params);
    } else if (this.isActionMode) {
      request$ = this.documentsService.addInitiativeDocument(this.actionIds[0], params);
    } else if (this.isPerimeterMode) {
      request$ = this.documentsService.addPerimeterDocument(this.perimeterIds[0], params);
    } else {
      const selectedInvest = this.investments.find(inv => inv.id === values.investId);
      if (!selectedInvest) {
        this.loading = false;
        return;
      }
      request$ = this.documentsService.addDocument(selectedInvest.id, params);
    }

    request$.subscribe({
      next: () => {
        this.loading = false;
        this.documentsService.clearDocumentsCache();
        this.documentsService.documentsHaveChanged();
        this.setSaved();
      },
      error: () => {
        this.loading = false;
        this.saveToast.close();
        this.cdr.detectChanges();
      },
    });
  }

  public onCompanyInputChange(value: string): void {
    if (!value || value.trim() === '') {
      this.filteredCorporates = [];
    } else {
      this.filteredCorporates = this.corporateNames.filter(name =>
        name.toLowerCase().includes(value.toLowerCase())
      );
    }
  }

  public onCompanyInputFocus(value: string): void {
    if (!value || value.trim() === '') {
      this.filteredCorporates = [...this.corporateNames];
    } else {
      this.onCompanyInputChange(value);
    }
  }

  public selectCorporate(name: string): void {
    this.form.get('company')?.setValue(name);
    this.filteredCorporates = [];
  }

  public onCompanyBlur(): void {
    setTimeout(() => { this.filteredCorporates = []; }, 200);
  }

  private confirmClose(): void {
    if (this.loading || this.isConfirmDialogOpen) return;

    if (this.form?.dirty) {
      this.isConfirmDialogOpen = true;
      this.dialogService.open(ConfirmationDialogComponent, {
        autoFocus: false,
        closeOnBackdropClick: false,
        context: {
          title: this.translate.instant('Save modifications'),
          label: this.translate.instant('Would you like to save your changes ?'),
          showThreeButtons: true,
        },
      }).onClose.subscribe((result: boolean | string) => {
        this.isConfirmDialogOpen = false;
        if (result === true) {
          if (this.isEditing) {
            this.addDocument();
          } else if (this.isStep1Valid() && (!this.isMaintenanceContract || this.step === 2)) {
            this.addDocument();
          } else {
            this.ref.close();
          }
        } else if (result === 'no') {
          this.ref.close();
        }
      });
    } else {
      this.ref.close();
    }
  }

  public close(): void {
    this.confirmClose();
  }

  private setSaving(): void {
    if (this.saveStatusTimer) {
      clearTimeout(this.saveStatusTimer);
      this.saveStatusTimer = null;
    }
    this.saveToast.showSaving();
    this.savingStartTime = Date.now();
  }

  private setSavedAndClose(result?: any): void {
    const elapsed = Date.now() - this.savingStartTime;
    const minDuration = 300;
    const remaining = Math.max(0, minDuration - elapsed);
    if (this.saveStatusTimer) {
      clearTimeout(this.saveStatusTimer);
    }
    this.saveStatusTimer = setTimeout(() => {
      this.saveToast.showSaved();

      this.saveStatusTimer = setTimeout(() => {
        this.ref.close(result);
      }, 1000);
    }, remaining);
  }

  private setSaved(): void {
    const elapsed = Date.now() - this.savingStartTime;
    const minDuration = 300;
    const remaining = Math.max(0, minDuration - elapsed);
    if (this.saveStatusTimer) {
      clearTimeout(this.saveStatusTimer);
    }
    this.saveStatusTimer = setTimeout(() => {
      this.saveToast.showSaved();

      this.saveStatusTimer = setTimeout(() => {
        this.ref.close();
      }, 1000);
    }, remaining);
  }

  public ngOnDestroy(): void {
    if (this.saveStatusTimer) {
      clearTimeout(this.saveStatusTimer);
    }
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

}
